import { collectTouch, getVisitorId } from "./collector";

export const BARE_IFRAME_WAIT_MS = 5000;
const CONFIG_SELECTOR = 'meta[name="od-attribution"]';

const readMarker = () => {
  const node = document.querySelector(CONFIG_SELECTOR);
  if (!node) return null;
  const type = node.getAttribute("data-ctx-type");
  const rawId = node.getAttribute("data-ctx-id");
  if (!type || !rawId) return null;
  const id = /^\d+$/.test(rawId) ? Number(rawId) : rawId;
  return { ctx: { type, id }, touch: node.getAttribute("data-ctx-touch") !== "false" };
};

const isFramed = () => {
  try {
    return window.top !== window;
  } catch (e) {
    return true;
  }
};

export const parentIsSameOrigin = () => {
  const ancestors = window.location.ancestorOrigins;
  if (ancestors) return ancestors[0] === window.location.origin;
  try {
    return window.parent.location.origin === window.location.origin;
  } catch (e) {
    return false;
  }
};

const PAYMENT_RETURN_ORIGINS = ["https://hooks.stripe.com"];

const referrerOrigin = () => {
  try {
    return new URL(document.referrer).origin;
  } catch (e) {
    return null;
  }
};

const navigatedWithinFrame = () => {
  const origin = referrerOrigin();
  return origin === window.location.origin || PAYMENT_RETURN_ORIGINS.includes(origin);
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const visitorMessage = (event) => {
  const payload = event && event.data;
  return (
    event.source === window.parent &&
    Boolean(payload) &&
    typeof payload === "object" &&
    payload.command === "setAttributionVisitor"
  );
};

const storeVisitorId = (payload) => {
  const uuid = payload && payload.data && payload.data.visitorUuid;
  if (typeof uuid !== "string" || !UUID_PATTERN.test(uuid)) return;
  if (window.__odAttrCollected) return;
  if (!window.__odAttribution) window.__odAttribution = { id: null, written: false };
  window.__odAttribution.id = uuid;
};

const sendOnce = (ctx, surface, options = {}) => {
  if (window.__odAttrCollected) return;
  window.__odAttrCollected = true;
  collectTouch({ origin: window.location.origin, ctx, surface, ...options });
};

const LINK_PARAMS = ["odt", "tl"];

const hasLinkParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return LINK_PARAMS.some((key) => params.has(key));
  } catch (e) {
    return false;
  }
};

const establishVisitor = (ctx) => {
  if (hasLinkParams()) {
    sendOnce(ctx, "hosted", { linkOnly: true });
  } else {
    getVisitorId();
  }
};

const announceToParent = (ctx, touch) => {
  try {
    window.parent.postMessage({ from: "od_attribution", command: "attributionEnabled", ctx, touch }, "*");
  } catch (e) {}
};

const REANNOUNCE_INTERVAL_MS = 1000;

const handshakeWithParent = (ctx, { touch, onNoReply }) => {
  let replied = false;
  let timer = null;
  let announcer = null;

  const onMessage = (event) => {
    if (!visitorMessage(event)) return;
    storeVisitorId(event.data);
    if (event.data.unsolicited === true) return;
    replied = true;
    clearTimeout(timer);
    clearInterval(announcer);
  };
  window.addEventListener("message", onMessage, false);
  announceToParent(ctx, touch);

  const startWindow = () => {
    if (replied) return;
    announceToParent(ctx, touch);
    announcer = setInterval(() => announceToParent(ctx, touch), REANNOUNCE_INTERVAL_MS);
    timer = setTimeout(() => {
      clearInterval(announcer);
      if (!replied) onNoReply();
    }, BARE_IFRAME_WAIT_MS);
  };

  if (document.readyState === "complete") {
    startWindow();
  } else {
    window.addEventListener("load", startWindow, { once: true });
  }
};

export const startHostedCollection = () => {
  try {
    if (document.prerendering) {
      document.addEventListener("prerenderingchange", startHostedCollection, { once: true });
      return;
    }

    const marker = readMarker();
    if (!marker) return;

    if (!isFramed()) {
      if (marker.touch) {
        sendOnce(marker.ctx, "hosted");
      } else {
        establishVisitor(marker.ctx);
      }
      return;
    }
    if (parentIsSameOrigin()) return;

    const sendsBareTouch = marker.touch && !navigatedWithinFrame();
    const onNoReply = sendsBareTouch ? () => sendOnce(marker.ctx, "iframe_bare") : () => getVisitorId();
    handshakeWithParent(marker.ctx, { touch: marker.touch, onNoReply });
  } catch (e) {}
};

export const startWhenReady = () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startHostedCollection, { once: true });
  } else {
    startHostedCollection();
  }
};
